import { isMessengerPath } from '@/modules/chat-matrix-fast/utils/messengerRoute'
import { trimDetectedUrl } from '@/modules/chat-matrix-fast/utils/messageRichText'

/** TODO: вернуть UI и join по ссылкам-приглашениям */
export const GROUP_INVITE_UI_ENABLED = false

/** Метаданные invite-ссылки (title/type в query — без отдельного preview API). */
export interface GroupInviteLinkMeta {
  chatId: number
  title?: string
  type?: string
}

/** Достаёт id группы из invite/share URL мессенджера. */
function parseGroupInviteChatId(url: string): number | null {
  return parseGroupInviteMeta(url)?.chatId ?? null
}

export function parseGroupInviteMeta(url: string): GroupInviteLinkMeta | null {
  try {
    const parsed = new URL(
      url.trim(),
      typeof window !== 'undefined' ? window.location.origin : 'https://local',
    )
    if (!isMessengerPath(parsed.pathname)) {
      return null
    }

    const chatRaw = parsed.searchParams.get('chat')
      || parsed.searchParams.get('amp;chat')
    const chatId = Number(chatRaw)
    if (!Number.isFinite(chatId) || chatId <= 0) {
      return null
    }

    const tab = parsed.searchParams.get('tab')
    const invite = parsed.searchParams.get('invite')

    if (tab === 'events') {
      return null
    }

    if (!(invite === '1' || invite === 'true' || tab === 'chats' || !tab)) {
      return null
    }

    const title = parsed.searchParams.get('title')?.trim() || undefined
    const type = parsed.searchParams.get('gtype')?.trim() || undefined

    return { chatId, title, type }
  } catch {
    return null
  }
}

export function isGroupInviteUrl(url: string): boolean {
  return parseGroupInviteChatId(url) != null
}

/** Убирает invite-URL из текста сообщения (в бабле ссылку не показываем). */
export function stripGroupInviteUrls(text: string): string {
  if (!GROUP_INVITE_UI_ENABLED || !text) {
    return text || ''
  }

  return text
    .replace(/https?:\/\/[^\s<>"']+/gi, (match, offset, source) => {
      const charBefore = offset > 0 ? source[offset - 1] : ''
      const cleaned = trimDetectedUrl(match, charBefore)
      return isGroupInviteUrl(cleaned) ? '' : match
    })
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]{2,}/g, ' ')
    .trim()
}

/** Перед отправкой дописывает title/type в invite-URL, чтобы получатель видел название. */
export function enrichGroupInviteUrlsInText(
  text: string,
  resolveMeta: (chatId: number) => { title?: string; type?: string } | null,
): string {
  if (!GROUP_INVITE_UI_ENABLED || !text) {
    return text
  }

  return text.replace(/https?:\/\/[^\s<>"']+/gi, (match, offset, source) => {
    const charBefore = offset > 0 ? source[offset - 1] : ''
    const cleaned = trimDetectedUrl(match, charBefore)
    const trailing = match.slice(cleaned.length)
    const parsed = parseGroupInviteMeta(cleaned)
    if (!parsed) {
      return match
    }
    if (parsed.title?.trim()) {
      return match
    }

    const extra = resolveMeta(parsed.chatId)
    const title = extra?.title?.trim()
    if (!title) {
      return match
    }

    try {
      const url = new URL(cleaned)
      url.searchParams.set('title', title.slice(0, 120))
      const type = extra?.type?.trim()
      if (type) {
        url.searchParams.set('gtype', type.slice(0, 32))
      }
      return `${url.toString()}${trailing}`
    } catch {
      return match
    }
  })
}
